<!-- Loading state -->
@if (isLoading) {
  <div style="padding: 40px; text-align: center;">
    <p>Loading mixing ticket...</p>
  </div>
}

<!-- Ticket content (visible once data is loaded) -->
@if (!isLoading) {
<div class="ticket-page">

  <!-- ============================================================
       TOP SECTION — MIXING TICKET (Operator Copy)
       ============================================================ -->
  <div class="ticket-section mixing-ticket-section">

    <!-- Header band -->
    <app-print-company-header title="Mixing Ticket">
      @if (ActiveDocumentVersion?.FormatNo) {
        <div class="format-info">
          {{ ActiveDocumentVersion.FormatNo }}, Rev {{ ActiveDocumentVersion.VersionNo || '00' }}
        </div>
      }
    </app-print-company-header>

    <!-- Order metadata grid -->
    @if (data) {
      <table class="metadata-table">
        <tr>
          <td class="meta-label">Plan No / SO#</td>
          <td class="meta-value">{{ data.SaleOrderNumber }}</td>
          <td class="meta-label">Ticket No</td>
          <td class="meta-value ticket-number">{{ data.MasterMixBatchNumber }}</td>
        </tr>
        <tr>
          <td class="meta-label">Article</td>
          <td class="meta-value">{{ data.ProductName }}</td>
          <td class="meta-label">Date</td>
          <td class="meta-value">{{ ticketDateFormatted }}</td>
        </tr>
        <tr>
          <td class="meta-label">Color</td>
          <td class="meta-value">{{ data.ColorName }}</td>
          <td class="meta-label">Item Code</td>
          <td class="meta-value">{{ data.FormulationCode }}</td>
        </tr>
        <tr>
          <td class="meta-label">Mixing</td>
          <td class="meta-value">{{ mixingLabel }}</td>
          <td class="meta-label">GSM</td>
          <td class="meta-value">{{ data.Gsm ?? '—' }}</td>
        </tr>
        <tr>
          <td class="meta-label">Quantity (Meters)</td>
          <td class="meta-value">{{ data.OrderQuantity ?? '—' }}</td>
          <td class="meta-label">Vessel</td>
          <td class="meta-value">{{ data.VesselName || '—' }}</td>
        </tr>
      </table>
    }

    <!-- Materials table -->
    @if (data) {
      <table class="materials-table">
        <thead>
          <tr class="materials-header">
            <th class="col-sno">S.No</th>
            <th class="col-material">Material</th>
            <th class="col-partno">Batch</th>
            <th class="col-phr">Qty in Parts (Ratio)</th>
            <th class="col-qty">Quantity (kg)</th>
            <th class="col-unit">Unit</th>
          </tr>
        </thead>
        <tbody>
          <!-- Base Materials -->
          @if (data.BaseMaterials && data.BaseMaterials.length > 0) {
            <tr>
              <td colspan="6" class="section-separator">BASE MATERIALS</td>
            </tr>
          }
          @for (m of data.BaseMaterials; track m.SerialNo; let i = $index) {
            <tr>
              <td class="col-sno">{{ i + 1 }}</td>
              <td class="col-material">
                {{ m.Material }}
              </td>
              <td class="col-partno">
                @if (m.Batches && m.Batches.length > 0) {
                  @for (b of m.Batches; track $index) {
                    <div class="batch-line">
                      <div class="batch-line-primary">{{ b.Batch || '—' }}</div>
                      @if (b.ShortCode || b.SerialNo) {
                        <div class="batch-line-label">
                          {{ b.ShortCode || '' }}{{ b.ShortCode && b.SerialNo ? ' · ' : '' }}{{ b.SerialNo || '' }}
                        </div>
                      }
                    </div>
                  }
                } @else {
                  —
                }
              </td>
              <td class="col-phr">{{ m.QtyInParts != null ? (m.QtyInParts | number:'1.2-3') : '—' }}</td>
              <td class="col-qty">{{ m.Quantity | number:'1.3-3' }}</td>
              <td class="col-unit">{{ m.Unit }}</td>
            </tr>
          }

          <!-- Pigments -->
          @if (data.Pigments && data.Pigments.length > 0) {
            <tr>
              <td colspan="6" class="section-separator pigments-separator">PIGMENTS</td>
            </tr>
          }
          @for (m of data.Pigments; track m.SerialNo; let i = $index) {
            <tr>
              <td class="col-sno">{{ (data.BaseMaterials?.length ?? 0) + i + 1 }}</td>
              <td class="col-material">
                {{ m.Material }}
              </td>
              <td class="col-partno">
                @if (m.Batches && m.Batches.length > 0) {
                  @for (b of m.Batches; track $index) {
                    <div class="batch-line">
                      <div class="batch-line-primary">{{ b.Batch || '—' }}</div>
                      @if (b.ShortCode || b.SerialNo) {
                        <div class="batch-line-label">
                          {{ b.ShortCode || '' }}{{ b.ShortCode && b.SerialNo ? ' · ' : '' }}{{ b.SerialNo || '' }}
                        </div>
                      }
                    </div>
                  }
                } @else {
                  —
                }
              </td>
              <td class="col-phr">{{ m.QtyInParts != null ? (m.QtyInParts | number:'1.2-3') : '—' }}</td>
              <td class="col-qty">{{ m.Quantity | number:'1.3-3' }}</td>
              <td class="col-unit">{{ m.Unit }}</td>
            </tr>
          }

          <!-- Totals row -->
          <tr class="totals-row">
            <td colspan="4" class="totals-label">Total Paste Weight</td>
            <td class="col-qty totals-value">
              {{ data.TotalPasteKg != null ? (data.TotalPasteKg | number:'1.3-3') : '—' }}
            </td>
            <td class="col-unit">kg</td>
          </tr>

          <!-- Unplanned RM additions: material put into the vessel after Mark Ready — kept
               after the recipe total so "Total Paste Weight" above reads as the recipe
               standard (TotalPasteKg) only, not inflated by post-Ready additions. -->
          @if (data.UnplannedAdditions && data.UnplannedAdditions.length > 0) {
            <tr>
              <td colspan="6" class="section-separator">UNPLANNED RM ADDITIONS</td>
            </tr>
          }
          @for (a of data.UnplannedAdditions; track $index; let i = $index) {
            <tr>
              <td class="col-sno">{{ i + 1 }}</td>
              <td class="col-material">
                {{ a.ProductName }}
                <div class="batch-line-label">{{ a.ReasonLabel }} · {{ a.AddedDate | date:'dd-MMM-yyyy HH:mm' }} · {{ a.AddedByName }}</div>
              </td>
              <td class="col-partno">
                @for (b of a.Batches; track $index) { <div class="batch-line-primary">{{ b }}</div> } @empty { — }
              </td>
              <td class="col-phr">—</td>
              <td class="col-qty">{{ a.Quantity | number:'1.3-3' }}</td>
              <td class="col-unit">{{ a.Unit }}</td>
            </tr>
          }
        </tbody>
      </table>
    }

    <!-- No data fallback -->
    @if (!data) {
      <div style="padding: 20px; text-align: center; color: #888;">
        Ticket data not available.
      </div>
    }

    <!-- Signature lines -->
    <div class="signature-section">
      <div class="signature-box">
        <div class="signature-line"></div>
        <div class="signature-label">Production Supervisor</div>
        <div class="signature-name">{{ data?.SupervisorName || '—' }}</div>
      </div>
      <div class="signature-box">
        <div class="signature-line"></div>
        <div class="signature-label">Tech Manager</div>
      </div>
    </div>

    <app-print-footer audience="internal"></app-print-footer>
  </div><!-- end .mixing-ticket-section -->

  <!-- ============================================================
       BOTTOM SECTION — VESSEL TAG (Tear-off)
       ============================================================ -->
  @if (data) {
    <div class="ticket-section vessel-tag-section">

      <div class="vessel-tag-heading">VESSEL TAG</div>

      <div class="vessel-tag-body">
        <div class="vessel-tag-left">
          <table class="vessel-tag-table">
            <tr>
              <td class="vt-label">Product Code</td>
              <td class="vt-value">{{ data.SaleOrderCode || '—' }}</td>
            </tr>
            <tr>
              <td class="vt-label">SF Code</td>
              <td class="vt-value">{{ data.FormulationCode }}</td>
            </tr>
            <tr>
              <td class="vt-label">Ticket No</td>
              <td class="vt-value ticket-number">{{ data.MasterMixBatchNumber }}</td>
            </tr>
            <tr>
              <td class="vt-label">Date</td>
              <td class="vt-value">{{ ticketDateFormatted }}</td>
            </tr>
            <tr>
              <td class="vt-label">Mixing</td>
              <td class="vt-value">{{ mixingLabel }}</td>
            </tr>
            <tr>
              <td class="vt-label">Vessel</td>
              <td class="vt-value">{{ data.VesselName || '—' }} {{ data.VesselTag ? '(' + data.VesselTag + ')' : '' }}</td>
            </tr>
          </table>

          <!-- Weight box -->
          <div class="weight-box">
            <div class="weight-box-label">Total Weight (kg)</div>
            <div class="weight-box-value">
              {{ data.TotalPasteKg != null ? (data.TotalPasteKg | number:'1.3-3') : '___________' }}
            </div>
          </div>
        </div>

        <!-- QR Code -->
        <div class="vessel-tag-right">
          <!-- nz-qrcode is available via NzQRCodeModule in PmsUINzAntModule -->
          <nz-qrcode
            [nzValue]="data.MasterMixBatchNumber"
            [nzSize]="120"
            [nzBordered]="true"
            [nzLevel]="'M'">
          </nz-qrcode>
          <div class="qr-label">{{ data.MasterMixBatchNumber }}</div>
        </div>
      </div>

    </div><!-- end .vessel-tag-section -->
  }

</div><!-- end .ticket-page -->
}
